'use client';

import React, { useState } from 'react';

interface SignupPageProps {
  onNavigate: (page: string) => void;
}

const SignupPage: React.FC<SignupPageProps> = ({ onNavigate }) => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    signupType: '',
    gradeLevel: '',
    notes: '',
    subjects: [] as string[],
  });
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      subjects: e.target.checked
        ? [...prev.subjects, val]
        : prev.subjects.filter(s => s !== val),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToPolicy) {
      setError('You must agree to the cancellation policy to continue.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch('https://formspree.io/f/xwvwnzyb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const perks = [
    { icon: '🎯', title: 'Personalized Matching', desc: 'We pair you with a tutor based on your learning style, goals, and schedule.' },
    { icon: '📅', title: 'Flexible Scheduling', desc: 'Sessions available 7 days a week, mornings through evenings.' },
    { icon: '💯', title: 'Satisfaction Guarantee', desc: "Not happy with your first session? We\'ll find you a new tutor at no charge." },
    { icon: '🔒', title: 'Safe & Secure', desc: 'All tutors are background-checked and certified. Your safety is our priority.' },
  ];

  const subjectOptions = [
    { value: 'English, LA & Social Studies K–12', label: 'English, LA & Social Studies K–12' },
    { value: 'Math & Science K–12', label: 'Math & Science K–12' },
    { value: 'College & University Level', label: 'College & University Level' },
    { value: 'Adult Learning', label: 'Adult Learning' },
  ];

  return (
    <div
      className="signup-layout"
      style={{
        gap: '3rem',
        alignItems: 'start',
        padding: '3rem 1.5rem',
        maxWidth: 1200,
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {/* Left: Info */}
      <div className="signup-info-col" style={{ position: 'sticky', top: 80 }}>
        <div className="section-label">Join Stellar Scholar</div>
        <h2 className="section-title" style={{ fontFamily: "'Playfair Display', serif" }}>
          Start Your Learning Journey
        </h2>
        <div className="gold-line" />
        <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '2rem', maxWidth: 560, lineHeight: 1.7 }}>
          Fill out the form and we&rsquo;ll match you with the perfect tutor within 24 hours.
        </p>

        {perks.map(perk => (
          <div key={perk.title} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
            <div
              style={{
                width: 44, height: 44,
                background: 'rgba(244,196,48,0.12)',
                border: '1px solid rgba(244,196,48,0.25)',
                borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.2rem', flexShrink: 0,
              }}
            >
              {perk.icon}
            </div>
            <div>
              <h4 style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.3rem', color: '#fff' }}>{perk.title}</h4>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{perk.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Right: Form */}
      <div>
        {submitted ? (
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20,
              padding: '2.5rem',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌟</div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', color: '#f4c430', marginBottom: '0.5rem' }}>
              You&rsquo;re In!
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
              We&rsquo;ll be in touch within 24 hours to match you with your perfect tutor.
            </p>
            <button
              className="btn-primary"
              style={{ marginTop: '2rem' }}
              onClick={() => onNavigate('home')}
            >
              Back to Home
            </button>
          </div>
        ) : (
          <div
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20,
              padding: '2rem',
            }}
          >
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', marginBottom: '0.5rem', color: '#fff' }}>
              Create Your Account
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginBottom: '2rem' }}>
              Takes less than 2 minutes to get started
            </p>

            <form onSubmit={handleSubmit}>
              <div className="signup-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>First Name</label>
                  <input type="text" name="firstName" placeholder="Jane" value={formData.firstName} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input type="text" name="lastName" placeholder="Smith" value={formData.lastName} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>Email Address</label>
                <input type="email" name="email" placeholder="jane@example.com" value={formData.email} onChange={handleChange} />
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>Phone Number</label>
                <input type="tel" name="phone" placeholder="(555) 000-0000" value={formData.phone} onChange={handleChange} />
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>I am signing up as a...</label>
                <select name="signupType" value={formData.signupType} onChange={handleChange}>
                  <option value="">Select one</option>
                  <option>Student (signing up for myself)</option>
                  <option>Parent (signing up my child)</option>
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>Grade / Level</label>
                <select name="gradeLevel" value={formData.gradeLevel} onChange={handleChange}>
                  <option value="">Select level</option>
                  <option>Elementary (PreK–Grade 6)</option>
                  <option>Junior High (7–9)</option>
                  <option>High School (10–12)</option>
                  <option>College / University</option>
                  <option>Adult Learner</option>
                </select>
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>Subjects Needed (select all that apply)</label>
                <div className="subjects-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginTop: '0.5rem' }}>
                  {subjectOptions.map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', lineHeight: 1.4 }}>
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={formData.subjects.includes(opt.value)}
                        onChange={handleCheckbox}
                        style={{ width: 16, height: 16, accentColor: '#f4c430', cursor: 'pointer', flexShrink: 0, marginTop: 2 }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '1.2rem' }}>
                <label>Anything else we should know?</label>
                <textarea
                  name="notes"
                  placeholder="Learning goals, schedule preferences, specific challenges..."
                  value={formData.notes}
                  onChange={handleChange}
                />
              </div>

              <div
                style={{
                  marginTop: '1.5rem',
                  background: 'rgba(244,196,48,0.06)',
                  border: '1px solid rgba(244,196,48,0.25)',
                  borderRadius: 12,
                  padding: '1rem 1.1rem',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.75)',
                    lineHeight: 1.6,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreedToPolicy}
                    onChange={e => setAgreedToPolicy(e.target.checked)}
                    required
                    style={{
                      width: 18,
                      height: 18,
                      accentColor: '#f4c430',
                      cursor: 'pointer',
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                  <span>
                    <span style={{ color: '#f4c430', fontWeight: 600 }}>Cancellation Policy — </span>
                    Appointments must be cancelled with more than 24 hours&rsquo; notice or the full rate will be charged. Exceptions include unavoidable/unpredictable emergencies. All efforts are made to remind clients of appointments in advance.{' '}
                    <strong style={{ color: '#fff' }}>Do you agree to these terms?</strong>
                    {' '}<span style={{ color: '#f87171' }}>*</span>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                className="btn-primary"
                style={{ width: '100%', justifyContent: 'center', marginTop: '1.5rem', padding: '1rem', fontSize: '1rem' }}
                disabled={isLoading}
              >
                {isLoading ? 'Sending…' : 'Create My Account →'}
              </button>
              {error && (
                <p style={{ color: '#f87171', marginTop: '0.75rem', fontSize: '0.875rem', textAlign: 'center' }}>{error}</p>
              )}
            </form>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .signup-name-grid {
            grid-template-columns: 1fr !important;
          }
          .subjects-grid {
            grid-template-columns: 1fr !important;
          }
          .signup-info-col {
            position: static !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SignupPage;
